import type { ImportFileType } from '../types';
import { ICON_FALLBACKS } from '../../../utils/iconFallbacks';

export interface ImportTemplateDefinition {
  id: ImportFileType;
  labelKey: string;
  descriptionKey: string;
  iconName: string;
  iconPath?: string;
  requiredColumns: string[];
  optionalColumns: string[];
  sampleRows: Record<string, string | number>[];
  acceptedExtensions: string[];
}

export const IMPORT_TEMPLATES: Record<ImportFileType, ImportTemplateDefinition> = {
  rides: {
    id: 'rides',
    labelKey: 'importWizard.fileTypes.rides.label',
    descriptionKey: 'importWizard.fileTypes.rides.description',
    iconName: 'route',
    requiredColumns: ['origin_lat', 'origin_lng', 'dest_lat', 'dest_lng', 'request_time'],
    optionalColumns: ['ride_id', 'user_id', 'standard_seat', 'wheelchair', 'request_type', 'from_address', 'to_address'],
    sampleRows: [
      {
        origin_lat: -33.8688, origin_lng: 151.2093,
        dest_lat: -33.8568, dest_lng: 151.2153,
        request_time: '2024-01-15T08:30:00.000',
        ride_id: 'RIDE-001', user_id: 'USER-001',
        standard_seat: 1, wheelchair: 0,
        request_type: 'departure_fixed',
        from_address: '1 Market St, Sydney', to_address: '100 George St, Sydney',
      },
      {
        origin_lat: -33.8750, origin_lng: 151.2070,
        dest_lat: -33.8600, dest_lng: 151.2100,
        request_time: '2024-01-15T09:15:00.000',
        ride_id: 'RIDE-002', user_id: 'USER-002',
        standard_seat: 2, wheelchair: 0,
        request_type: 'departure_fixed',
        from_address: '50 Park Ave, Sydney', to_address: '200 Pitt St, Sydney',
      },
      {
        origin_lat: -33.8800, origin_lng: 151.2000,
        dest_lat: -33.8700, dest_lng: 151.2200,
        request_time: '2024-01-15T14:00:00.000',
        ride_id: 'RIDE-003', user_id: 'USER-003',
        standard_seat: 1, wheelchair: 1,
        request_type: 'arrival_fixed',
        from_address: '10 Broadway, Sydney', to_address: '75 Elizabeth St, Sydney',
      },
    ],
    acceptedExtensions: ['.csv', '.xlsx', '.xls'],
  },
  points: {
    id: 'points',
    labelKey: 'importWizard.fileTypes.points.label',
    descriptionKey: 'importWizard.fileTypes.points.description',
    iconName: 'location-dot',
    requiredColumns: ['latitude', 'longitude'],
    optionalColumns: ['name', 'description'],
    sampleRows: [
      { latitude: -33.8688, longitude: 151.2093, name: 'Central Station', description: 'Main train station' },
      { latitude: -33.8568, longitude: 151.2153, name: 'Circular Quay', description: 'Ferry terminal' },
      { latitude: -33.8750, longitude: 151.2070, name: 'Town Hall', description: 'City center hub' },
    ],
    acceptedExtensions: ['.csv', '.xlsx', '.xls', '.kml'],
  },
  polygons: {
    id: 'polygons',
    labelKey: 'importWizard.fileTypes.polygons.label',
    descriptionKey: 'importWizard.fileTypes.polygons.description',
    iconName: 'polygon',
    iconPath: ICON_FALLBACKS.PentagonOutlined,
    requiredColumns: ['wkt'],
    optionalColumns: ['name'],
    sampleRows: [
      {
        name: 'Service Zone A',
        wkt: 'POLYGON((151.20 -33.86, 151.22 -33.86, 151.22 -33.88, 151.20 -33.88, 151.20 -33.86))',
      },
      {
        name: 'Service Zone B',
        wkt: 'POLYGON((151.18 -33.87, 151.20 -33.87, 151.20 -33.89, 151.18 -33.89, 151.18 -33.87))',
      },
    ],
    acceptedExtensions: ['.csv', '.xlsx', '.xls', '.kml'],
  },
  fleet_shifts: {
    id: 'fleet_shifts',
    labelKey: 'importWizard.fileTypes.fleet_shifts.label',
    descriptionKey: 'importWizard.fileTypes.fleet_shifts.description',
    iconName: 'bus',
    requiredColumns: ['vehicle_id', 'shift_start', 'shift_end', 'start_lat', 'start_lng', 'end_lat', 'end_lng', 'standard_seats'],
    optionalColumns: ['wheelchair_seats', 'break_start', 'break_end', 'break_lat', 'break_lng', 'number_of_vehicles'],
    sampleRows: [
      { vehicle_id: 'VAN-001', shift_start: '06:00', shift_end: '14:00', start_lat: -33.8833, start_lng: 151.2056, end_lat: -33.8833, end_lng: 151.2056, standard_seats: 12, wheelchair_seats: 0, break_start: '10:00', break_end: '10:30', break_lat: -33.8833, break_lng: 151.2056 },
      { vehicle_id: 'VAN-002', shift_start: '14:00', shift_end: '22:00', start_lat: -33.8736, start_lng: 151.2069, end_lat: -33.8736, end_lng: 151.2069, standard_seats: 12, wheelchair_seats: 2, break_start: '18:00', break_end: '18:30', break_lat: -33.8736, break_lng: 151.2069 },
      { vehicle_id: 'BUS-001', shift_start: '07:00', shift_end: '15:00', start_lat: -33.8614, start_lng: 151.2131, end_lat: -33.8614, end_lng: 151.2131, standard_seats: 24, wheelchair_seats: 1, break_start: '11:00', break_end: '11:30', break_lat: -33.8614, break_lng: 151.2131 },
    ],
    acceptedExtensions: ['.csv', '.xlsx', '.xls'],
  },
  gtfs: {
    id: 'gtfs',
    labelKey: 'importWizard.fileTypes.gtfs.label',
    descriptionKey: 'importWizard.fileTypes.gtfs.description',
    iconName: 'train',
    requiredColumns: [],
    optionalColumns: [],
    sampleRows: [],
    acceptedExtensions: ['.zip'],
  },
};

export function getTemplateForType(fileType: ImportFileType): ImportTemplateDefinition {
  return IMPORT_TEMPLATES[fileType];
}

export function getAllTemplates(): ImportTemplateDefinition[] {
  return Object.values(IMPORT_TEMPLATES);
}
